import React, { useState, useEffect } from 'react'
import { useLazyLoadQuery, graphql } from 'react-relay/hooks'
import CreateReportSelectSource from './CreateReportSelectSource'
import SelectUploadSource from './SelectUploadSource'
import IntegrationReport from './IntegrationReport'
import FileUploadReport from './FileUploadReport'
import { useAuth } from 'hooks'
import { equalGroups } from '../utils/templates'
import ConnectionChoices from '../../integrations/ConnectionChoices'

export default function CreateReportModal({
  modal,
  modalBehavior,
  integrationResult,
  orgSlug,
  companyId,
  editing,
  setEditing,
}) {
  const { currentOrg, currentCompany } = useAuth()

  let defaultChoice = { kind: 'intuit' }

  if (integrationResult?.integrationKind) {
    defaultChoice = { kind: integrationResult.integrationKind }
  } else if (integrationResult?.integrationId) {
    defaultChoice = { rowId: integrationResult.integrationId }
  }

  //org and company from auth are null momentarily when returning from an integration,
  //which causes the query to throw an error. Pass query params in that situation
  const slug = currentOrg?.slug || orgSlug
  const compId = currentCompany?.id || companyId

  const data = useLazyLoadQuery(
    graphql`
      query CreateReportModalQuery($slug: String!, $compId: ID!) {
        organizationBySlug(slug: $slug) {
          defaultReportSections
          ...SelectTemplateModalHeader_organization
        }
        companyById(id: $compId) {
          ...IntegrationChoices_company
        }
      }
    `,
    { slug, compId },
  )

  const [choice, setChoice] = useState(defaultChoice)
  const [uploadSource, setUploadSource] = useState('qbo')
  const [step, setStep] = useState(0)
  const [defaultReportSections, setDefaultReportSections] = useState(null)
  const [defaultTemplate, setDefaultTemplate] = useState(null)
  const [currentTemplate, setCurrentTemplate] = useState(null)
  const [configSections, setConfigSections] = useState(null)
  const [noCurrentTenant, setNoCurrentTenant] = useState(false)

  useEffect(() => {
    if (!data) {
      return
    }

    const defaultReportSections = data.organizationBySlug.defaultReportSections

    const sections = [
      ...defaultReportSections.anomalies,
      ...defaultReportSections.financialReports,
      ...defaultReportSections.metadata,
    ]

    const defaultTemplate = {
      name: 'default',
      config: {
        sections,
      },
    }

    setDefaultReportSections(defaultReportSections)
    setDefaultTemplate(defaultTemplate)
    setCurrentTemplate(defaultTemplate)
    setConfigSections(sections)
  }, [data])

  useEffect(() => {
    if (configSections && currentTemplate?.config?.sections) {
      setEditing(!equalGroups(configSections, currentTemplate.config.sections))
    }
  }, [configSections, currentTemplate, setEditing])

  useEffect(() => {
    if (!choice?.id) {
      return
    }

    if (choice.kind === 'xero' && !choice.config.xero.currentTenant) {
      setNoCurrentTenant(true)
    }
  }, [choice, setNoCurrentTenant])

  const handleTemplateChange = (template) => {
    setCurrentTemplate(template)
    setConfigSections(template.config.sections)
  }

  const handleBack = () => {
    modal.closeAllOtherModals()
    setStep(step - 1)
  }

  const handleNext = () => {
    modal.closeAllOtherModals()
    setStep(step + 1)
  }

  if (!data) {
    return null
  }

  if (step === 0) {
    return (
      <CreateReportSelectSource
        modal={modalBehavior}
        onNext={handleNext}
        selected={choice}
        onChange={setChoice}
        integrationResult={integrationResult}
        company={data.companyById}
      />
    )
  }

  if (step === 1 && noCurrentTenant) {
    return (
      <ConnectionChoices
        integrationRowId={choice.rowId}
        onDone={() => {
          setNoCurrentTenant(false)
        }}
        onClose={modalBehavior.onClose}
      />
    )
  }

  if (choice === 'upload') {
    if (step === 1) {
      return (
        <SelectUploadSource
          modal={modalBehavior}
          onNext={handleNext}
          onBack={handleBack}
          selected={uploadSource}
          onChange={setUploadSource}
        />
      )
    }

    return (
      <FileUploadReport
        modal={modalBehavior}
        step={step}
        onNext={handleNext}
        onBack={handleBack}
        uploadSource={uploadSource}
        currentTemplate={currentTemplate}
        handleTemplateChange={handleTemplateChange}
        configSections={configSections}
        setConfigSections={setConfigSections}
        editing={editing}
        defaultReportSections={defaultReportSections}
        defaultTemplate={defaultTemplate}
        organization={data.organizationBySlug}
      />
    )
  }

  return (
    <IntegrationReport
      modal={modalBehavior}
      onBack={handleBack}
      step={step}
      onNext={handleNext}
      integration={choice}
      currentTemplate={currentTemplate}
      handleTemplateChange={handleTemplateChange}
      configSections={configSections}
      setConfigSections={setConfigSections}
      editing={editing}
      defaultReportSections={defaultReportSections}
      defaultTemplate={defaultTemplate}
      organization={data.organizationBySlug}
    />
  )
}
